// ============================================================
// eventLog — 汎用イベントロガー（ループエンジニアリング基盤）
//
// 設計: booking-platform/md/loop-engineering.md
//
// 何のためにあるか:
//   「成功したふりをするバグ」を外から読める形にするため。
//   例: 2026-07 のサイドパネル メール送信ログは、保存 API が 200 を返しつつ
//       追記だけが捨てられていた。例外が出ないので console にも何も出ない。
//       → 行動と**その結果**をサーバに残せば、後から気付ける。
//
// 使い方:
//   logEvent({ action: 'email.send', target: id, result: 'ok', detail: { template } });
//   logEvent({ action: 'memo.append', target: id, result: 'fail', err: String(e) });
//
// ルール:
//   - result は必ず入れる（'ok' | 'fail'）。結果のないログは半分しか役に立たない
//   - detail に顧客の個人情報（氏名・メール・電話）を入れない。target=予約ID で辿る
//   - fire-and-forget。ログ送信の失敗が業務を止めてはならない
// ============================================================

import { SYS_API_BASE } from '../constants/calendar';
import { isTrainingMode } from '../training/trainingMode';

export type EventResult = 'ok' | 'fail';

export interface EventLogInput {
  /** ドット区切りの固定語彙。例: 'email.send' / 'memo.append' / 'reservation.save' */
  action: string;
  /** 対象の識別子（予約ID等）。個人情報は入れない */
  target?: string | number | null;
  result: EventResult;
  /** 所要時間(ms)。計測していなければ省略可 */
  ms?: number;
  /** 集計に使う構造化データ。個人情報は入れない */
  detail?: Record<string, any>;
  /** 失敗時のエラー内容 */
  err?: unknown;
}

const ENDPOINT = `${SYS_API_BASE}/log_event.php`;

/** セッションID: 同一タブ内の一連の操作を後から辿るための識別子 */
const sessionId: string = (() => {
  try {
    const KEY = 'bo_event_sid';
    const existing = sessionStorage.getItem(KEY);
    if (existing) return existing;
    const sid = `s_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;
    sessionStorage.setItem(KEY, sid);
    return sid;
  } catch {
    return 's_nostore';
  }
})();

/**
 * アプリ版。version.json を一度だけ読んでキャッシュする。
 * 取得前のイベントは ver 空で送られるが、それは許容する
 * （ログのために業務の起動を待たせない）。
 */
let appVersion = '';
try {
  fetch('/version.json', { cache: 'no-store' })
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => { if (typeof j?.version === 'string') appVersion = j.version; })
    .catch(() => { /* ignore */ });
} catch { /* ignore */ }

/** 操作者名（memoLog と同じ解決順） */
function resolveStaffName(): string {
  if (typeof window === 'undefined') return '';
  if ((window as any).bo_staff?.name) return (window as any).bo_staff.name;
  try {
    const saved = localStorage.getItem('bo_staff');
    if (saved) return JSON.parse(saved).name || '';
  } catch { /* ignore */ }
  return '';
}

/** エラーオブジェクトを短い文字列にする */
function toErrString(err: unknown): string | undefined {
  if (err == null) return undefined;
  if (typeof err === 'string') return err.slice(0, 2000);
  if (err instanceof Error) return `${err.name}: ${err.message}`.slice(0, 2000);
  try {
    return JSON.stringify(err).slice(0, 2000);
  } catch {
    return String(err).slice(0, 2000);
  }
}

/**
 * イベントを1件記録する。fire-and-forget（await 不要・例外を投げない）。
 *
 * ⚠️ catch 節で握りつぶすだけの実装は禁止。必ずここに result:'fail' を流すこと。
 *    ブラウザの console は誰も見ないため、握りつぶしと同じになる。
 */
export function logEvent(input: EventLogInput): void {
  try {
    const body = JSON.stringify({
      ts: new Date().toISOString(),
      app: 'r-cal',
      ver: appVersion,
      sid: sessionId,
      staff: resolveStaffName(),
      page: typeof window !== 'undefined' ? window.location.pathname : '',
      action: input.action,
      target: input.target != null ? String(input.target) : '',
      result: input.result,
      ms: typeof input.ms === 'number' ? Math.round(input.ms) : undefined,
      detail: input.detail,
      err: toErrString(input.err),
    });

    // 失敗イベントは console にも出す（開発中に即気付けるように）
    if (input.result === 'fail') {
      console.warn('[event:fail]', input.action, input.target ?? '', input.err ?? '');
    }

    // トレーニングモードの操作は本番の操作ログに混ぜない（2026-08-24）
    if (isTrainingMode()) return;

    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
      return;
    }
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => { /* ignore */ });
  } catch {
    // ログ基盤の失敗は業務に影響させない
  }
}

/**
 * 非同期処理を包んで成否と所要時間を自動記録する。
 *
 *   const r = await trackEvent('invoice.create', id, () => createInvoice(...));
 *
 * 例外はそのまま再スローする（記録するだけで、制御は変えない）。
 */
export async function trackEvent<T>(
  action: string,
  target: string | number | null | undefined,
  fn: () => Promise<T>,
  detail?: Record<string, any>,
): Promise<T> {
  const started = Date.now();
  try {
    const result = await fn();
    logEvent({ action, target, result: 'ok', ms: Date.now() - started, detail });
    return result;
  } catch (e) {
    logEvent({ action, target, result: 'fail', ms: Date.now() - started, detail, err: e });
    throw e;
  }
}

/**
 * 未捕捉例外・未処理 Promise 拒否をイベントとして拾う。
 * アプリ起動時に一度だけ呼ぶ。
 */
export function installGlobalErrorLogging(): void {
  if (typeof window === 'undefined') return;
  if ((window as any).__eventLogInstalled) return;
  (window as any).__eventLogInstalled = true;

  // ハートビート（2026-08-02 追加）
  // ⚠️ これが無いと、レポートの「イベントログなし」が
  //      (a) その日は誰も使っていない
  //      (b) ログ基盤そのものが壊れている
  //    のどちらなのか区別できない。観測基盤自身の無言の失敗を防ぐための1発。
  //    セッション（タブ）ごとに1回だけなので量は増えない。
  try {
    const KEY = 'bo_event_opened';
    if (!sessionStorage.getItem(KEY)) {
      sessionStorage.setItem(KEY, '1');
      logEvent({ action: 'app.open', result: 'ok' });
    }
  } catch {
    logEvent({ action: 'app.open', result: 'ok' });
  }

  window.addEventListener('error', (ev) => {
    logEvent({
      action: 'js.error',
      result: 'fail',
      err: ev.error ?? ev.message,
      detail: { src: `${ev.filename || ''}:${ev.lineno || 0}` },
    });
  });

  window.addEventListener('unhandledrejection', (ev) => {
    logEvent({ action: 'js.unhandledrejection', result: 'fail', err: ev.reason });
  });
}
